import { formatMoney, formatPercent, formatRatioPercent } from "@/lib/format";
import { MetricField } from "@/components/dashboard/metrics-games/common";
import { average, median } from "@/components/dashboard/metrics-games/utils";
import type { StartupRow } from "@/components/dashboard/metrics-games/types";

type Props = {
  startups: StartupRow[];
  onUpdateStartupNumber: (index: number, key: "mrr" | "growth30d", value: number) => void;
  onUpdateStartupSale: (index: number, onSale: boolean) => void;
  onRemoveStartup: (index: number) => void;
  onAddStartup: () => void;
  onRandomize: () => void;
  onReset: () => void;
};

export function GameNicheBuilder({
  startups,
  onUpdateStartupNumber,
  onUpdateStartupSale,
  onRemoveStartup,
  onAddStartup,
  onRandomize,
  onReset
}: Props) {
  const mrrValues = startups.map((startup) => Math.max(0, startup.mrr));
  const growthValues = startups.map((startup) => startup.growth30d);
  const startupCount = startups.length;
  const totalMrr = mrrValues.reduce((sum, value) => sum + value, 0);
  const averageMrr = startupCount > 0 ? totalMrr / startupCount : 0;
  const medianMrr = median(mrrValues.filter((value) => value > 0));
  const averageGrowth30d = average(growthValues);
  const startupsForSale = startups.filter((startup) => startup.onSale).length;
  const forSaleRatio = startupCount > 0 ? startupsForSale / startupCount : 0;
  const top3Mrr = [...mrrValues].sort((a, b) => b - a).slice(0, 3).reduce((sum, value) => sum + value, 0);
  const top3MrrShare = totalMrr > 0 ? top3Mrr / totalMrr : 0;

  return (
    <article className="card metrics-game">
      <div className="metrics-game-head">
        <p className="section-label">Game 1</p>
        <h2 className="card-title">Build a niche from startup inputs</h2>
      </div>
      <p className="card-desc">
        Edit MRR, growth, and sale status. Add or remove startups to manage the niche.
      </p>

      <div className="metrics-startup-grid metrics-startup-grid--two-col">
        {startups.map((startup, index) => (
          <div key={startup.id} className="metrics-startup-row">
            <div className="metrics-startup-row-header">
              <span className="metrics-startup-id">Startup {startup.id}</span>
              <button
                type="button"
                className="metrics-startup-remove"
                onClick={() => onRemoveStartup(index)}
                disabled={startups.length <= 1}
                aria-label={`Remove startup ${startup.id}`}
                title="Remove startup"
              >
                ×
              </button>
            </div>
            <div className="metrics-input-group">
              <label className="form-label">MRR ($)</label>
              <input
                type="number"
                className="form-input metrics-input"
                value={startup.mrr}
                min={0}
                onChange={(event) => onUpdateStartupNumber(index, "mrr", Number(event.target.value))}
              />
            </div>
            <div className="metrics-input-group">
              <label className="form-label">Growth 30d (%)</label>
              <input
                type="number"
                className="form-input metrics-input"
                value={startup.growth30d}
                onChange={(event) => onUpdateStartupNumber(index, "growth30d", Number(event.target.value))}
              />
            </div>
            <button
              type="button"
              className={`metrics-on-sale-toggle ${startup.onSale ? "metrics-on-sale-toggle--active" : ""}`}
              onClick={() => onUpdateStartupSale(index, !startup.onSale)}
              aria-pressed={startup.onSale}
              aria-label={startup.onSale ? "On sale (click to mark not for sale)" : "Not on sale (click to mark for sale)"}
            >
              On sale
            </button>
          </div>
        ))}
      </div>

      <div className="metrics-actions">
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={onAddStartup}
          disabled={startups.length >= 10}
        >
          + Add startup
        </button>
        <button type="button" className="btn btn--ghost btn--sm" onClick={onRandomize}>
          Shuffle scenario
        </button>
        <button type="button" className="btn btn--ghost btn--sm" onClick={onReset}>
          Reset example
        </button>
      </div>

      <div className="score-breakdown-grid">
        <MetricField label="Startup count" value={String(startupCount)} />
        <MetricField label="Total MRR" value={formatMoney(totalMrr)} />
        <MetricField label="Average MRR" value={formatMoney(averageMrr)} />
        <MetricField label="Median MRR" value={formatMoney(medianMrr)} />
        <MetricField label="Average growth 30d" value={formatPercent(averageGrowth30d)} />
        <MetricField label="For-sale ratio" value={formatRatioPercent(forSaleRatio)} />
        <MetricField label="Top 3 MRR share" value={formatRatioPercent(top3MrrShare)} />
        <MetricField label="Startups for sale" value={`${startupsForSale}`} />
      </div>

      <p className="metrics-formula">
        Formula highlights:
        <code> medianMrr = median(mrr &gt; 0)</code> |
        <code> forSaleRatio = startupsForSale / startupCount</code> |
        <code> top3MrrShare = top3Mrr / totalMrr</code>
      </p>
    </article>
  );
}
